import { useEffect, useRef, useState } from 'react';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import { OrderModel } from 'lib/model/objects/orderModel';
import { logAndCaptureCriticalError } from 'utils';
import { ColumnService } from 'lib/services/directory';
import {
  StripeOperationType,
  StripeOperationEvent,
  ProcessableStatus
} from 'lib/types/events';
import { getFirebaseContext } from 'utils/firebase';
import { requestNewOrderInvoice } from '../helpers/paymentProcessing';
import { PlacementFlowStep } from '../placementFlowStep';

/**
 * Manages the full order invoice flow for the placement form: determines whether
 * a new invoice is required, watches the Firestore stripe operation event, and
 * auto-triggers invoice creation once the user reaches the Summary step.
 *
 * TODO: Split this up using Redux-toolkit slices
 */
export const useOrderInvoiceFlow = ({
  orderModel,
  isEditing,
  version,
  step,
  isPastContentStep
}: {
  orderModel: OrderModel;
  isEditing: boolean;
  version: number;
  step: PlacementFlowStep;
  isPastContentStep: boolean;
}) => {
  const [requireNewInvoice, setRequireNewInvoice] = useState(false);
  const [isRequestingInvoice, setIsRequestingInvoice] = useState(false);
  /**
   * Tracks whether a request has been attempted in this forward-navigation session.
   * Prevents an unbounded retry loop when the API call fails: rather than
   * re-triggering on every re-render after isRequestingInvoice resets to false,
   * we stay blocked until the user navigates back (which resets this flag).
   */
  const [invoiceRequestAttempted, setInvoiceRequestAttempted] = useState(false);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [newInvoiceId, setInvoiceId] = useState<string | null>(null);

  const isInvoiceLoading =
    !invoiceError && (isRequestingInvoice || (isPastContentStep && isCreating));

  // Watch the latest ORDER_INVOICE_CREATION stripe operation event
  useEffect(() => {
    const unsubscribe = getFirebaseContext()
      .eventsRef<StripeOperationEvent>()
      .where(
        'data.operationType',
        '==',
        StripeOperationType.ORDER_INVOICE_CREATION
      )
      .where('data.orderId', '==', orderModel.id)
      .where('data.version', '==', version)
      .orderBy('createdAt', 'desc')
      .limit(1)
      .onSnapshot(async eventsSnap => {
        if (!eventsSnap.docs.length) {
          setIsCreating(false);
          return;
        }

        const latestEvent = eventsSnap.docs[0].data();

        if (latestEvent.status === ProcessableStatus.SUCCESS) {
          setIsCreating(false);
          const [err, invoice] = await orderModel.getInvoice(version);
          if (!err && invoice) {
            setInvoiceId(invoice.id);
          } else {
            setInvoiceError('Invoice creation failed. Please try again.');
          }
          setIsRequestingInvoice(false);
          return;
        }

        if (latestEvent.status === ProcessableStatus.PERMANENT_FAILURE) {
          setIsCreating(false);
          setInvoiceError('Invoice creation failed. Please try again.');
          setIsRequestingInvoice(false);
          return;
        }

        // IN_PROGRESS or freshly created (no status yet)
        setIsCreating(true);
      });

    return () => {
      unsubscribe();
    };
  }, [orderModel.id, version]);

  const { isLoading: requireNewInvoiceLoading } = useAsyncEffect({
    fetchData: async () => {
      if (step !== PlacementFlowStep.Summary) {
        setRequireNewInvoice(false);
        return;
      }
      if (!isEditing) {
        setRequireNewInvoice(true);
        return;
      }

      const [requiresNewErr, requiresNew] =
        await orderModel.editRequiresNewInvoice(version);

      if (requiresNewErr) {
        // Fail-closed: block invoice creation rather than risk double-invoicing.
        setRequireNewInvoice(false);
        logAndCaptureCriticalError(
          ColumnService.PAYMENTS,
          requiresNewErr,
          'Failed to determine whether editing order requires new invoice'
        );
        setInvoiceError(
          'An unexpected error occurred. Please go to the previous step and try again.'
        );
        return;
      }

      setRequireNewInvoice(requiresNew);
    },
    dependencies: [step, version]
  });

  const discardInvoice = () => {
    setInvoiceRequestAttempted(false);
    setInvoiceError(null);
    setInvoiceId(null);
  };

  // Reset invoice state when the user navigates back, allowing a fresh
  // attempt on re-entry to the Summary step.
  useEffect(() => {
    if (!isPastContentStep) {
      discardInvoice();
    }
  }, [isPastContentStep]);

  // Auto-trigger invoice creation once the user reaches the Summary step.
  const requestInvoice = async (force = false) => {
    setInvoiceRequestAttempted(true);
    setInvoiceError(null);
    setIsRequestingInvoice(true);
    const [error] = await requestNewOrderInvoice(orderModel.id, version, force);
    if (error) {
      setInvoiceError('Failed to submit order. Please try again.');
      setIsRequestingInvoice(false);
    }
    // On success, stay true until invoiceId arrives to prevent a duplicate
    // call during the gap between the API response and the Firestore snapshot.
  };

  // Stable ref so the auto-trigger effect closure always calls the latest version
  // without needing requestInvoice in its dependency array.
  const requestInvoiceRef = useRef(requestInvoice);
  requestInvoiceRef.current = requestInvoice;

  useEffect(() => {
    if (
      requireNewInvoice &&
      isPastContentStep &&
      !newInvoiceId &&
      !isInvoiceLoading &&
      !invoiceRequestAttempted &&
      !invoiceError
    ) {
      void requestInvoiceRef.current();
    }
  }, [
    requireNewInvoice,
    isPastContentStep,
    newInvoiceId,
    isInvoiceLoading,
    invoiceRequestAttempted,
    invoiceError
  ]);

  return {
    requireNewInvoice,
    isLoading: requireNewInvoiceLoading || isInvoiceLoading,
    error: invoiceError,
    newInvoiceId,
    setNewInvoiceId: (id: string) => setInvoiceId(id),
    discardInvoice
  };
};
